import React from 'react'
import { ErrorBoundary } from './components'
import { MailboxIcon, CheckIcon, CloseIcon } from '@bizfirst/common-react'

const subscriberPatterns = [
  {
    pattern: '*',
    description: 'Subscribe to all topics - matches any message',
    example: 'subscribe("*", callback)',
  },
  {
    pattern: 'topic.subtopic',
    description: 'Subscribe to specific topic - exact match required',
    example: 'subscribe("form.submit", callback)',
  },
  {
    pattern: 'topic.*',
    description: 'Subscribe to topic with wildcard - matches all subtopics',
    example: 'subscribe("form.*", callback)',
  },
  {
    pattern: 'topic.sub.*',
    description: 'Subscribe to nested topics with wildcard matching',
    example: 'subscribe("data.user.*", callback)',
  },
]

export function SubscribersReferencePage(): React.ReactElement {
  return (
    <ErrorBoundary>
      <div className="page-container subscribers-page">
        <div className="page-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><MailboxIcon /> Available Subscribers</h2>
        <p className="page-description">
          Complete reference guide to message subscription patterns and subscriber API for the pub/sub system. Subscribers
          listen for messages on specific topics with support for wildcard matching.
        </p>
      </div>

      {/* Subscription Patterns Section */}
      <section className="section patterns-section">
        <h3>Subscription Patterns</h3>
        <p className="section-description">
          The subscription system supports flexible topic matching with wildcards. Choose the pattern that best fits
          your message routing needs.
        </p>

        <div className="patterns-container">
          {subscriberPatterns.map((pattern) => (
            <div key={pattern.pattern} className="pattern-card">
              <div className="pattern-header">
                <h4 className="pattern-name">
                  <code>{pattern.pattern}</code>
                </h4>
              </div>
              <p className="pattern-description">{pattern.description}</p>
              <div className="pattern-example">
                <strong>Example:</strong>
                <code className="example-code">{pattern.example}</code>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Subscription API Section */}
      <section className="section api-section">
        <h3>Subscription API</h3>
        <p className="section-description">
          Use the SubscriptionManager to interact with the pub/sub system. Each server instance has its own
          subscription manager.
        </p>

        <div className="subscription-details">
          <div className="details-grid">
            <div className="detail-item">
              <h5>Subscribe to Topic</h5>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Listen for messages on a specific topic pattern
              </p>
              <pre>
                <code>{`const subscription = streamInstance
  .getServer('serverId')
  .subscriptionManager
  .subscribe('topic.name', (envelope) => {
    // Handle message
  })`}</code>
              </pre>
            </div>
            <div className="detail-item">
              <h5>Publish to Topic</h5>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Send a message to all subscribers on a topic
              </p>
              <pre>
                <code>{`await streamInstance
  .getServer('serverId')
  .subscriptionManager
  .publish('topic.name', envelope)`}</code>
              </pre>
            </div>
            <div className="detail-item">
              <h5>Unsubscribe</h5>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Stop listening to messages on a topic
              </p>
              <pre>
                <code>{`subscription.unsubscribe()`}</code>
              </pre>
            </div>
            <div className="detail-item">
              <h5>Get Active Subscriptions</h5>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                View all currently active subscriptions
              </p>
              <pre>
                <code>{`const active = streamInstance
  .getServer('serverId')
  .subscriptionManager
  .subscriptions`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Subscriber Flow Section */}
      <section className="section flow-section">
        <h3>Message Delivery Flow</h3>
        <p className="section-description">Subscribers receive messages that match their topic patterns:</p>

        <div className="flow-diagram">
          <div className="flow-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Message Published</h4>
              <p>Message is sent to a topic via PublishToSubscribersHook</p>
            </div>
          </div>

          <div className="flow-arrow">↓</div>

          <div className="flow-step highlight">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Pattern Matching</h4>
              <p>Topic is matched against all active subscriptions</p>
            </div>
          </div>

          <div className="flow-arrow">↓</div>

          <div className="flow-step highlight">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Wildcard Resolution</h4>
              <p>Wildcard patterns are evaluated (*, topic.*, topic.sub.*)</p>
            </div>
          </div>

          <div className="flow-arrow">↓</div>

          <div className="flow-step highlight">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>Subscriber Notification</h4>
              <p>All matching subscribers receive the message</p>
            </div>
          </div>

          <div className="flow-arrow">↓</div>

          <div className="flow-step terminal">
            <div className="step-number">5</div>
            <div className="step-content">
              <h4>Callback Execution</h4>
              <p>Subscriber callback function is invoked with message</p>
            </div>
          </div>
        </div>

        <div className="flow-notes">
          <p>
            <strong>Note:</strong> Multiple subscribers can listen to the same topic. A single message can be delivered
            to multiple subscribers if their patterns match. Subscribers are matched in registration order.
          </p>
        </div>
      </section>

      {/* Pattern Matching Examples Section */}
      <section className="section examples-section">
        <h3>Pattern Matching Examples</h3>
        <p className="section-description">
          See how different patterns match against published topics:
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Pattern: <code>form.*</code></h4>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Matches:</p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li style={{ margin: '4px 0' }}><code>form.submit</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>form.validate</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>form.error</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>form.user.submit</code> <CloseIcon /> (too deep)</li>
            </ul>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Pattern: <code>data.user.*</code></h4>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Matches:</p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li style={{ margin: '4px 0' }}><code>data.user.created</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>data.user.updated</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>data.user.deleted</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>data.user.profile.updated</code> <CloseIcon /> (too deep)</li>
            </ul>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Pattern: <code>*</code></h4>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Matches:</p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li style={{ margin: '4px 0' }}><code>anything</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>any.topic</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}><code>any.nested.topic</code> <CheckIcon /></li>
              <li style={{ margin: '4px 0' }}>Nothing is excluded <CheckIcon /></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Best Practices Section */}
      <section className="section practices-section">
        <h3>Best Practices</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Use Specific Patterns</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Prefer specific topic patterns like <code>form.submit</code> over wildcards to reduce message overhead and
              improve performance.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Unsubscribe When Done</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Always unsubscribe from topics when they're no longer needed to free resources and prevent memory leaks.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Handle Errors in Callbacks</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Wrap subscriber callbacks in try-catch to prevent one subscriber from affecting others.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Use Consistent Naming</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Use hierarchical naming like <code>domain.action.detail</code> for topics to make patterns intuitive.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Monitor Active Subscriptions</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Use the monitoring tools to track active subscriptions and diagnose delivery issues.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
            <h4 style={{ margin: '0 0 12px 0' }}><CheckIcon />Test Wildcard Patterns</h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Verify your wildcard patterns match expected topics. Use the Subscribers page to monitor delivery.
            </p>
          </div>
          </div>
        </section>
      </div>
    </ErrorBoundary>
  )
}
